import React, { useEffect, useMemo, useRef, useState } from 'react';

interface CelebrationConfettiProps {
  active: boolean;
  onComplete?: () => void;
}

const COLORS = ['#616FC9', '#9675C9', '#C9619C', '#44C2A4', '#C9A061'];
const PIECE_COUNT = 60;
const DISPLAY_DURATION_MS = 3600;

const CelebrationConfetti: React.FC<CelebrationConfettiProps> = ({ active, onComplete }) => {
  const [visible, setVisible] = useState(active);
  const onCompleteRef = useRef(onComplete);
  const pieces = useMemo(() => Array.from({ length: PIECE_COUNT }, (_, index) => ({
    color: COLORS[index % COLORS.length],
    delay: (index % 7) * 0.1,
    duration: 2 + (index % 12) * 0.2,
    left: (index * 37) % 100,
    size: 6 + (index % 7),
  })), []);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!active) {
      setVisible(false);
      return undefined;
    }

    setVisible(true);
    const timer = window.setTimeout(() => {
      setVisible(false);
      onCompleteRef.current?.();
    }, DISPLAY_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [active]);

  if (!visible) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none"
      data-testid="celebration-confetti"
      style={{ zIndex: 2600 }}
    >
      {pieces.map((piece, index) => (
        <span
          className="obd-confetti-piece"
          data-testid="confetti-piece"
          key={index}
          style={{
            animation: `obd-confetti ${piece.duration}s linear ${piece.delay}s both`,
            background: piece.color,
            height: piece.size,
            left: `${piece.left}vw`,
            width: piece.size,
          }}
        />
      ))}
    </div>
  );
};

export default CelebrationConfetti;
